import { supabase } from "./supabase";

// Business status types
export type BusinessStatus = 'pending' | 'active' | 'blocked' | 'unpublished';

export interface BusinessStatusInfo {
  status: BusinessStatus;
  label: string;
  color: string;
  description: string;
}

// Status configuration
export const BUSINESS_STATUS_CONFIG: Record<BusinessStatus, BusinessStatusInfo> = {
  pending: {
    status: 'pending',
    label: 'Pending',
    color: 'bg-yellow-100 text-yellow-800',
    description: 'Awaiting admin approval'
  },
  active: {
    status: 'active',
    label: 'Active',
    color: 'bg-green-100 text-green-800',
    description: 'Visible to customers'
  },
  blocked: {
    status: 'blocked',
    label: 'Blocked',
    color: 'bg-red-100 text-red-800',
    description: 'Hidden from customers'
  },
  unpublished: {
    status: 'unpublished',
    label: 'Unpublished',
    color: 'bg-gray-100 text-gray-800',
    description: 'Temporarily hidden'
  }
};

/**
 * Update the status of a business.
 * @param businessId - The business UUID
 * @param status - The new business status
 * @returns Promise with error or null
 */
export async function setBusinessStatus(businessId: string, status: BusinessStatus): Promise<null | string> {
  // Validate status
  if (!Object.keys(BUSINESS_STATUS_CONFIG).includes(status)) {
    return `Invalid status: ${status}`;
  }

  // Determine verified status based on business status
  const verified = status === 'active';

  const { error } = await supabase
    .from("businesses")
    .update({
      business_status: status,
      verified: verified,
      updated_at: new Date().toISOString()
    })
    .eq("id", businessId);

  if (error) {
    return error.message;
  }
  return null;
}

/**
 * Update the published status of a business (backwards compatibility).
 * @param businessId - The business UUID
 * @param published - true to publish (active), false to unpublish
 * @returns Promise with error or null
 */
export async function setBusinessPublished(businessId: string, published: boolean): Promise<null | string> {
  const status: BusinessStatus = published ? 'active' : 'unpublished';
  return setBusinessStatus(businessId, status);
}

/**
 * Approve a business (set to active status).
 * @param businessId - The business UUID
 * @returns Promise with error or null
 */
export async function approveBusinessApplication(businessId: string): Promise<null | string> {
  return setBusinessStatus(businessId, 'active');
}

/**
 * Block a business (set to blocked status).
 * @param businessId - The business UUID
 * @returns Promise with error or null
 */
export async function blockBusiness(businessId: string): Promise<null | string> {
  return setBusinessStatus(businessId, 'blocked');
}

/**
 * Reinstate a business (set to active status).
 * @param businessId - The business UUID
 * @returns Promise with error or null
 */
export async function reinstateBusiness(businessId: string): Promise<null | string> {
  return setBusinessStatus(businessId, 'active');
}

/**
 * Unpublish a business (set to unpublished status).
 * @param businessId - The business UUID
 * @returns Promise with error or null
 */
export async function unpublishBusiness(businessId: string): Promise<null | string> {
  return setBusinessStatus(businessId, 'unpublished');
}

/**
 * Get businesses by status with filtering options.
 * @param status - Filter by business status (optional)
 * @param limit - Limit number of results (optional)
 * @returns Promise with businesses array or error
 */
export async function getBusinessesByStatus(
  status?: BusinessStatus,
  limit?: number
): Promise<{ data: any[] | null; error: string | null }> {
  let query = supabase
    .from("businesses")
    .select(`
      *,
      owner:user_profiles(id, full_name)
    `)
    .order("updated_at", { ascending: false });

  if (status) {
    query = query.eq("business_status", status);
  }

  if (limit) {
    query = query.limit(limit);
  }

  const { data, error } = await query;

  return {
    data: data || [],
    error: error ? error.message : null
  };
}

/**
 * Update the monthly fee for a business.
 * @param businessId - The business UUID
 * @param monthlyFee - The new monthly fee (number)
 * @returns Promise with error or null
 */
export async function updateBusinessMonthlyFee(businessId: string, monthlyFee: number): Promise<null | string> {
  const { error } = await supabase
    .from("businesses")
    .update({ monthly_fee: monthlyFee })
    .eq("id", businessId);

  if (error) {
    return error.message;
  }
  return null;
}

/**
 * Get business status info for display.
 * @param status - The business status
 * @returns Status info object
 */
export function getBusinessStatusInfo(status: string): BusinessStatusInfo {
  return BUSINESS_STATUS_CONFIG[status as BusinessStatus] || BUSINESS_STATUS_CONFIG.pending;
}